import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import Loader from "./Loader";
import { Link } from "react-router-dom";
import {
  HIDDEN_KEYS,
  filterVisible,
  hideItem,
} from "../utils/hiddenHistory";
import {
  formatAppDateTime,
  todayLocalDateString,
} from "../utils/dateFormat";

const defaultSale = () => ({
  customer_id: "",
  stock_id: "",
  driver_name: "",
  sale_date: todayLocalDateString(),
  quantity: 1,
  unit_price: 0,
});

export default function Sales() {
  const [customers, setCustomers] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [sales, setSales] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [sale, setSale] = useState(defaultSale);
  const [loading, setLoading] = useState(true);
  const [showNewSale, setShowNewSale] = useState(false);
  const [listVersion, setListVersion] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const formatNumber = (value) => Number(value ?? 0).toFixed(2);

  const fetchData = async () => {
    try {
      const [customersRes, stocksRes, salesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/customers`),
        axios.get(`${API_BASE_URL}/api/stocks`),
        axios.get(`${API_BASE_URL}/api/sales`),
      ]);
      setCustomers(customersRes.data);
      setStocks(stocksRes.data);
      setSales(salesRes.data);
      const storedDrivers = JSON.parse(
        localStorage.getItem("lagerDrivers") || "[]",
      );
      setDrivers(Array.isArray(storedDrivers) ? storedDrivers : []);

      try {
        const salariesRes = await axios.get(`${API_BASE_URL}/api/salaries`);
        setEmployees(Array.isArray(salariesRes.data) ? salariesRes.data : []);
      } catch (salaryError) {
        console.warn("Could not load salary employee list", salaryError);
        setEmployees([]);
      }
    } catch (error) {
      console.error("Error fetching sales data", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSale({
      ...sale,
      [name]:
        name === "quantity" || name === "unit_price" ? Number(value) : value,
    });
  };

  const handleStockChange = (e) => {
    const stockId = e.target.value;
    const selected = stocks.find((item) => String(item.id) === String(stockId));
    setSale({
      ...sale,
      stock_id: stockId,
      unit_price: selected ? Number(selected.price) : 0,
    });
  };

  const handleCustomerFilterChange = (e) => {
    setSelectedCustomer(e.target.value);
  };

  const handleDriverFilterChange = (e) => {
    setSelectedDriver(e.target.value);
  };

  const employeeNames = [
    ...new Set(
      [
        ...employees.map((item) => item.employee_name),
        ...drivers.map((driver) => driver.name),
        ...sales.map((item) => item.driver_name).filter(Boolean),
      ]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    ),
  ];

  const filteredSales = filterVisible(
    sales.filter((item) => {
      const matchesCustomer = selectedCustomer
        ? String(item.customer_id) === String(selectedCustomer)
        : true;
      const matchesDriver = selectedDriver
        ? String(item.driver_name) === String(selectedDriver)
        : true;
      return matchesCustomer && matchesDriver;
    }),
    HIDDEN_KEYS.sales,
  );
  void listVersion;

  const totalForCustomer = filteredSales.reduce(
    (sum, item) => sum + Number(item.total_amount ?? 0),
    0,
  );

  const lineTotal =
    (Number(sale.quantity) || 0) * (Number(sale.unit_price) || 0);

  const closeNewSale = () => {
    setSale(defaultSale());
    setShowNewSale(false);
  };

  const deleteSale = (id) => {
    if (!window.confirm("Remove this sale from the list? You can restore it from History."))
      return;
    hideItem(HIDDEN_KEYS.sales, id);
    setListVersion((v) => v + 1);
  };

  const saveSale = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/sales`, sale);
      alert("Sale recorded successfully");
      setSale(defaultSale());
      setShowNewSale(false);
      fetchData();
    } catch (error) {
      console.error("Error recording sale", error);
      alert(
        "Failed to record sale. Check stock availability and required fields.",
      );
    }
  };

  if (loading) {
    return <Loader message="Loading sales" fullPage />;
  }

  return (
    <>
      <div className="container-fluid app-container">
        <div className="row">
          <div className="col-12">
            <div className="card app-screen-card">
              <div className="app-sales-toolbar">
                <button
                  type="button"
                  className="btn btn-primary app-btn"
                  onClick={() => setShowNewSale(true)}
                >
                  + New Sale
                </button>
                <Link to="/history" className="btn btn-outline-secondary app-btn ms-2">
                  History
                </Link>
                <div className="app-sales-filter">
                  <div className="row">
                  <div className="mb-3 col-12 col-md-6">
                    {/* <label className="form-label mb-1">
                      Filter by customer
                    </label> */}
                    <select
                      className="form-select"
                      value={selectedCustomer}
                      onChange={handleCustomerFilterChange}
                    >
                      <option value="">Filter All customers</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-0 col-12 col-md-6">
                    {/* <label className="form-label mb-1">Filter by driver</label> */}
                    <select
                      className="form-select"
                      value={selectedDriver}
                      onChange={handleDriverFilterChange}
                    >
                      <option value="">Filter All drivers</option>
                      {employeeNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                  </div>
                </div>
                <div className="app-sales-stats small">
                  <strong>Transactions:</strong> {filteredSales.length}
                  <span className="app-sales-stats__sep"> · </span>
                  <strong>Total:</strong> {formatNumber(totalForCustomer)}
                </div>
              </div>
              <div className="table-responsive app-table-wrap">
                <table className="table table-sm app-data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Village</th>
                      <th>Driver</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSales.map((item) => (
                      <tr key={item.id}>
                        <td data-label="Date">{formatAppDateTime(item.sale_date)}</td>
                        <td data-label="Customer">
                          {item.customer?.name || "-"}
                        </td>
                        <td data-label="Village">
                          {item.customer?.place || "-"}
                        </td>
                        <td data-label="Driver">{item.driver_name || "-"}</td>
                        <td data-label="Item">{item.stock?.name || "-"}</td>
                        <td data-label="Qty">{item.quantity}</td>
                        <td data-label="Unit Price">
                          {Number(item.unit_price).toFixed(2)}
                        </td>
                        <td data-label="Total" className="cell-highlight">
                          {Number(item.total_amount).toFixed(2)}
                        </td>
                        {/* <td data-label="Action">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteSale(item.id)}
                          >
                            Delete
                          </button>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showNewSale && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeNewSale();
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-sale-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="new-sale-modal-title">
                New Sale
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={closeNewSale}
              >
              X
              </button>
            </div>
            <form onSubmit={saveSale}>
              <div className="app-modal__body">
                <div className="mb-3">
                  <label className="form-label">Customer</label>
                  <select
                    className="form-select"
                    name="customer_id"
                    value={sale.customer_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} ({formatNumber(customer.balance)})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Driver name</label>
                  <select
                    className="form-select"
                    name="driver_name"
                    value={sale.driver_name}
                    onChange={handleChange}
                  >
                    <option value="">Select driver</option>
                    {employeeNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Stock Item</label>
                  <select
                    className="form-select"
                    name="stock_id"
                    value={sale.stock_id}
                    onChange={handleStockChange}
                    required
                  >
                    <option value="">Select stock item</option>
                    {stocks.map((stock) => (
                      <option key={stock.id} value={stock.id}>
                        {stock.name} - {stock.quantity} available
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Sale Date</label>
                  <input
                    className="form-control"
                    type="date"
                    name="sale_date"
                    value={sale.sale_date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">Quantity</label>
                    <input
                      className="form-control"
                      type="number"
                      name="quantity"
                      min="1"
                      value={sale.quantity}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">Unit Price</label>
                    <input
                      className="form-control"
                      type="number"
                      step="0.01"
                      name="unit_price"
                      value={sale.unit_price}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="rounded-3 p-3 bg-white border border-light">
                  <strong>Line total:</strong>{" "}
                  <span className="text-primary fs-5">
                    {formatNumber(lineTotal)}
                  </span>
                </div>
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeNewSale}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Sale
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
